import React from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { useSectionStore } from "src/store/croModule.state";
import styles from "./styles.module.scss";
import ProductDetailPage from "../../../Product";

interface SectionItem {
  id: string;
  component: React.ReactElement<any>;
}

interface SectionsProps {
  sectionRefs: React.MutableRefObject<Record<string, HTMLElement | null>>;
  isWhatIf: boolean;
}

const DraggableSection: React.FC<{
  section: SectionItem;
  index: number;
  prefix: string;
  sectionRefs: React.MutableRefObject<Record<string, HTMLElement | null>>;
}> = ({ section, index, prefix, sectionRefs }) => (
  <Draggable
    key={section.id}
    draggableId={`${prefix}-${section.id}`}
    index={index}
  >
    {(provided) => (
      <div
        className={styles.container}
        ref={(el) => {
          provided.innerRef(el);
          if (el) sectionRefs.current[`${prefix}-${section.id}`] = el;
        }}
        {...provided.draggableProps}
      >
        {React.cloneElement(section.component, {
          id: section.id,
          provided,
        })}
      </div>
    )}
  </Draggable>
);

const SectionList: React.FC<{
  droppableId: string;
  sections: SectionItem[];
  prefix: string;
  sectionRefs: React.MutableRefObject<Record<string, HTMLElement | null>>;
}> = ({ droppableId, sections, prefix, sectionRefs }) => (
  <Droppable droppableId={droppableId}>
    {(provided) => (
      <div
        className={styles.section}
        {...provided.droppableProps}
        ref={provided.innerRef}
      >
        {sections.map((section, idx) => (
          <DraggableSection
            key={section.id}
            section={section}
            index={idx}
            prefix={prefix}
            sectionRefs={sectionRefs}
          />
        ))}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
);

const Sections: React.FC<SectionsProps> = ({ sectionRefs, isWhatIf }) => {
  const sectionsBefore = useSectionStore((s) => s.sectionsBefore);
  const sectionsSide = useSectionStore((s) => s.sectionsSide);
  const sectionsAfter = useSectionStore((s) => s.sectionsAfter);
  const reorderSections = useSectionStore((s) => s.reorderSections);

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    reorderSections(result.source, result.destination);
  };

  return (
    <DragDropContext onDragEnd={handleOnDragEnd}>
      <SectionList
        droppableId="sectionsBefore"
        sections={sectionsBefore}
        prefix="before"
        sectionRefs={sectionRefs}
      />

      <div className={styles.productDetailContainer}>
        <ProductDetailPage
          isWhatIf={isWhatIf}
          sideSlot={
            <SectionList
              droppableId="sectionsSide"
              sections={sectionsSide}
              prefix="side"
              sectionRefs={sectionRefs}
            />
          }
        />
      </div>

      <SectionList
        droppableId="sectionsAfter"
        sections={sectionsAfter}
        prefix="after"
        sectionRefs={sectionRefs}
      />
    </DragDropContext>
  );
};

export default Sections;
